/**
 * Choosing a coverage option on the options and compare screens. Choosing adds the option to the plan
 * straight away (it counts before the consumer applies), then moves on. If a different option is
 * already in the plan, nothing happens until the consumer confirms replacing it: there is one added
 * quote at a time and it is never removed silently. A failed save keeps the consumer where they are.
 */
import { useState } from 'react';
import { useAddedQuote } from './use-added-quote';
import { useConsumerPolicies } from './use-consumer-policies';
import { useConsumerPlan } from './use-consumer-plan';

export interface PendingReplacement {
  quoteKey: string;
  currentPolicy: PolicyFundingSource;
  currentHasApplication: boolean;
}

export function useSelectQuote(clientId: string, onSelected: () => void) {
  const { plan } = useConsumerPlan(clientId);
  const { addedQuote } = useConsumerPolicies(clientId);
  const { addQuote, addingQuoteKey, addFailed } = useAddedQuote(clientId);
  const [pendingReplacement, setPendingReplacement] = useState<PendingReplacement | null>(null);

  const add = async (quoteKey: string) => {
    try {
      await addQuote(quoteKey);
      onSelected();
    } catch {
      // `addFailed` shows the error and the consumer stays to try again.
    }
  };

  const choose = (quoteKey: string) => {
    if (addedQuote && plan?.selectedQuoteKey !== quoteKey) {
      setPendingReplacement({
        quoteKey,
        currentPolicy: addedQuote.policy,
        currentHasApplication: addedQuote.status === 'application-submitted',
      });
      return;
    }
    void add(quoteKey);
  };

  const confirmReplacement = () => {
    if (!pendingReplacement) return;
    setPendingReplacement(null);
    void add(pendingReplacement.quoteKey);
  };

  return {
    choose,
    choosingQuoteKey: addingQuoteKey,
    chooseFailed: addFailed,
    pendingReplacement,
    confirmReplacement,
    cancelReplacement: () => setPendingReplacement(null),
  };
}
